import { EPaymentStatus } from '../../common/types';
import {
  EGiftCardTheme,
  EPromoCodeRefusal,
} from '../../services/giftCards/types';
import { giftCardMessages as messages } from './messages';

export const THEMES: {
  theme: EGiftCardTheme;
  title: typeof messages.themeEtretat;
}[] = [
  {
    theme: EGiftCardTheme.LAVANDE,
    title: messages.themeLavande,
  },
  {
    theme: EGiftCardTheme.ETRETAT,
    title: messages.themeEtretat,
  },
  {
    theme: EGiftCardTheme.CHAMBORD,
    title: messages.themeChambord,
  },
  {
    theme: EGiftCardTheme.MONT_SAINT_MICHEL,
    title: messages.themeMontSaintMichel,
  },
  {
    theme: EGiftCardTheme.ESTEREL,
    title: messages.themeEsterel,
  },
  {
    theme: EGiftCardTheme.COLMAR,
    title: messages.themeColmar,
  },
  {
    theme: EGiftCardTheme.VERDON,
    title: messages.themeVerdon,
  },
  {
    theme: EGiftCardTheme.PARIS,
    title: messages.themeParis,
  },
  {
    theme: EGiftCardTheme.AMSTERDAM,
    title: messages.themeAmsterdam,
  },
  {
    theme: EGiftCardTheme.KINDERDIJK,
    title: messages.themeKinderdijk,
  },
  {
    theme: EGiftCardTheme.BIG_BEN,
    title: messages.themeBigBen,
  },
  {
    theme: EGiftCardTheme.TOWER_BRIDGE,
    title: messages.themeTowerBridge,
  },
];

/**
 * @description A theme and its title: Étretat for an unknown one.
 * @param {string} theme
 */
export const getTheme = (theme: string) =>
  THEMES.find((item) => item.theme === theme) ?? THEMES[1];

type TMessage = typeof messages.occasionChristmas;

/**
 * The occasions of the landing chips. Those with a slug have their own page, /carte-cadeau/<slug>, for search engines:
 * its title and text, and a theme that fits.
 */
export const OCCASIONS: {
  slug: string | null;
  label: TMessage;
  theme: EGiftCardTheme;
  title?: TMessage;
  text?: TMessage;
}[] = [
  {
    slug: 'noel',
    label: messages.occasionChristmas,
    theme: EGiftCardTheme.COLMAR,
    title: messages.noelTitle,
    text: messages.noelText,
  },
  {
    slug: 'anniversaire',
    label: messages.occasionBirthday,
    theme: EGiftCardTheme.LAVANDE,
    title: messages.anniversaireTitle,
    text: messages.anniversaireText,
  },
  {
    slug: 'saint-valentin',
    label: messages.occasionValentine,
    theme: EGiftCardTheme.PARIS,
    title: messages.saintValentinTitle,
    text: messages.saintValentinText,
  },
  {
    slug: 'fete-des-meres',
    label: messages.occasionMothers,
    theme: EGiftCardTheme.ETRETAT,
    title: messages.feteDesMeresTitle,
    text: messages.feteDesMeresText,
  },
  {
    slug: 'fete-des-peres',
    label: messages.occasionFathers,
    theme: EGiftCardTheme.VERDON,
    title: messages.feteDesPeresTitle,
    text: messages.feteDesPeresText,
  },
  {
    slug: 'mariage',
    label: messages.occasionWedding,
    theme: EGiftCardTheme.AMSTERDAM,
    title: messages.mariageTitle,
    text: messages.mariageText,
  },
  {
    slug: 'retraite',
    label: messages.occasionRetirement,
    theme: EGiftCardTheme.ETRETAT,
    title: messages.retraiteTitle,
    text: messages.retraiteText,
  },
  {
    slug: null,
    label: messages.occasionJustBecause,
    theme: EGiftCardTheme.VERDON,
  },
];

/** The route of the occasion pages: only the known slugs, so that /carte-cadeau/create and the others stay theirs */
export const OCCASION_SLUGS = OCCASIONS.flatMap(({ slug }) =>
  slug ? [slug] : [],
);

/** The send times offered, every half hour from 7:00 to 22:00 */
export const TIMES = Array.from({ length: 31 }, (_, index) => {
  const minutes = 7 * 60 + index * 30;

  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
    minutes % 60,
  ).padStart(2, '0')}`;
});

/** The page asks the worker's last Payline status this often after the popin */
export const POLLING_INTERVAL_MS = 2000;
export const FAILED_PAYMENT_STATUSES = [
  EPaymentStatus.REFUSED,
  EPaymentStatus.CANCELLED,
  EPaymentStatus.ERROR,
];

export const PRESET_AMOUNTS = [25, 50, 100, 200];
export const MIN_AMOUNT = 10;
export const MAX_AMOUNT = 500;

/** The gift card code checked on /code-cadeau, filled in at the checkout */
export const STORED_CODE_KEY = 'tictactrip.giftCardCode';

/**
 * @description Remembers a gift card code for the checkout. The storage can be off (private mode): then nothing is kept.
 * @param {string} code
 */
export const storeGiftCardCode = (code: string): void => {
  try {
    window.localStorage.setItem(STORED_CODE_KEY, code);
  } catch {
    // No storage: the customer types the code again at the checkout
  }
};

/**
 * @description The code waiting for the checkout, left in place.
 * @returns {string | null}
 */
export const peekStoredGiftCardCode = (): string | null => {
  try {
    return window.localStorage.getItem(STORED_CODE_KEY);
  } catch {
    return null;
  }
};

/**
 * @description The gift card code kept from /code-cadeau, once: it is forgotten when read.
 * @returns {string | null}
 */
export const takeStoredGiftCardCode = (): string | null => {
  try {
    const code = window.localStorage.getItem(STORED_CODE_KEY);
    window.localStorage.removeItem(STORED_CODE_KEY);

    return code;
  } catch {
    return null;
  }
};

/**
 * @description The euros typed or picked, or null when out of range.
 * @param {number | 'custom'} choice
 * @param {string} custom
 * @returns {number | null}
 */
export const getAmountEuros = (
  choice: number | 'custom',
  custom: string,
): number | null => {
  const amount =
    choice === 'custom' ? Number(custom.replace(',', '.')) : choice;

  return Number.isInteger(amount) &&
    amount >= MIN_AMOUNT &&
    amount <= MAX_AMOUNT
    ? amount
    : null;
};

export const isEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

/** Why a code is refused, in the customer's words: several API reasons share a message */
export const REFUSAL_MESSAGES: Record<
  EPromoCodeRefusal,
  typeof messages.refusalUnknown
> = {
  [EPromoCodeRefusal.UNKNOWN]: messages.refusalUnknown,
  [EPromoCodeRefusal.DISABLED]: messages.refusalNotValid,
  [EPromoCodeRefusal.NOT_STARTED]: messages.refusalNotValid,
  [EPromoCodeRefusal.EXPIRED]: messages.refusalNotValid,
  [EPromoCodeRefusal.WRONG_BRAND]: messages.refusalScope,
  [EPromoCodeRefusal.EMAIL_NOT_ALLOWED]: messages.refusalEmail,
  [EPromoCodeRefusal.EMAIL_MISSING]: messages.refusalEmailMissing,
  [EPromoCodeRefusal.FIRST_ORDER_ONLY]: messages.refusalFirstOrder,
  [EPromoCodeRefusal.GLOBAL_LIMIT_REACHED]: messages.refusalUsedUp,
  [EPromoCodeRefusal.CUSTOMER_LIMIT_REACHED]: messages.refusalUsedUp,
  [EPromoCodeRefusal.BUDGET_SPENT]: messages.refusalUsedUp,
  [EPromoCodeRefusal.NO_BALANCE]: messages.refusalNoBalance,
  [EPromoCodeRefusal.MIN_ORDER_NOT_REACHED]: messages.refusalMinOrder,
  [EPromoCodeRefusal.CARRIER_EXCLUDED]: messages.refusalScope,
  [EPromoCodeRefusal.MODE_EXCLUDED]: messages.refusalScope,
  [EPromoCodeRefusal.TRAVEL_DATE_EXCLUDED]: messages.refusalScope,
  [EPromoCodeRefusal.ONE_PROMO_ONLY]: messages.refusalOnePromo,
  [EPromoCodeRefusal.NOT_STACKABLE]: messages.refusalNotStackable,
  [EPromoCodeRefusal.ALREADY_APPLIED]: messages.refusalAlreadyApplied,
  [EPromoCodeRefusal.NOTHING_TO_PAY]: messages.refusalNothingToPay,
  [EPromoCodeRefusal.ANCILLARY_MISSING]: messages.refusalAncillaryMissing,
  [EPromoCodeRefusal.EMAIL_NOT_VERIFIED]: messages.refusalEmailNotVerified,
  [EPromoCodeRefusal.OWN_OFFER_CARD]: messages.refusalOwnOfferCard,
};
